import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  TextField,
  Typography,
  Divider,
  Chip,
  LinearProgress,
  Alert,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Avatar,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import StraightenIcon from "@mui/icons-material/Straighten";
import ScaleIcon from "@mui/icons-material/Scale";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import axios from "axios";
import { useTranslation, Trans } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import PublicHeaderDark from "components/PublicHeaderDark";
import PublicFooterDark from "components/PublicFooterDark";
import RevealOnScroll from "components/RevealOnScroll";
import DarkHeroBackdrop from "components/DarkHeroBackdrop";
import { SG, BG, BODY_FONT, PURPLE_LIGHT } from "theme/publicDarkTokens";
// Shared with the staff calculator + mobile-staff (shared/shippingCalc.js).
import { calculateShipping } from "generated/shippingCalc";

const ruleMeta = (rule, t) => {
  switch (rule) {
    case "overweight":
      return { text: t("shippingCalculator.rule.overweight"), color: "warning", icon: <TrendingUpIcon fontSize="small" /> };
    case "normal":
      return { text: t("shippingCalculator.rule.normal"), color: "success", icon: <LocalShippingIcon fontSize="small" /> };
    case "discount1":
      return { text: t("shippingCalculator.rule.discount1"), color: "info", icon: <TrendingDownIcon fontSize="small" /> };
    case "discount2":
      return { text: t("shippingCalculator.rule.discount2"), color: "info", icon: <TrendingDownIcon fontSize="small" /> };
    default:
      return { text: "", color: "default", icon: null };
  }
};

const SectionLabel = ({ icon, text }) => (
  <Stack direction="row" alignItems="center" spacing={1} mb={1.2}>
    <Avatar sx={{ width: 28, height: 28, bgcolor: "secondary.main", color: "white" }}>
      {icon}
    </Avatar>
    <Typography variant="subtitle2" fontWeight={600} color="text.primary">
      {text}
    </Typography>
  </Stack>
);

function ShippingCalculator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const [rates, setRates] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dims, setDims] = useState({ l: "", w: "", h: "" });
  const [weight, setWeight] = useState("");

  const isPublic = !localStorage.getItem("customer");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get("/api/public/shipping-rates");
        if (cancelled) return;
        if (res.data?.success) setRates(res.data);
        else setError(res.data?.message || "Failed to load rates");
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const result = useMemo(
    () => calculateShipping(rates, dims, weight),
    [rates, dims, weight]
  );

  const currency = rates?.currency || "THB";
  const sizeUnit = rates?.sizeUnit || "cm";
  const weightUnit = rates?.weightUnit || "kg";

  const body = (
    <Box sx={{ background: BG, fontFamily: BODY_FONT }}>
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: BG,
          py: { xs: 6, md: 10 },
          textAlign: "center",
        }}
      >
        <DarkHeroBackdrop />

        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <RevealOnScroll y={28} delay={200} duration={650} enabled={!loading}>
            <Avatar
              sx={{
                width: 72, height: 72, mx: "auto", mb: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 12px 30px rgba(124,58,237,.4)",
              }}
            >
              <CalculateIcon sx={{ fontSize: 36 }} />
            </Avatar>
            <Typography
              variant={isMobile ? "h3" : "h2"}
              fontWeight="bold"
              gutterBottom
              sx={{
                fontFamily: SG,
                background: "linear-gradient(130deg, #C084FC 0%, #7C3AED 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("shippingCalculator.title")}
            </Typography>
            <Typography sx={{ fontSize: 17, color: "rgba(248,245,255,.56)", maxWidth: 640, mx: "auto" }}>
              {t("shippingCalculator.subtitle")}
            </Typography>
          </RevealOnScroll>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <RevealOnScroll y={20} delay={200}>
        <Box
          sx={{
            mb: 4,
            p: 2.5,
            borderRadius: 3,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            background: "rgba(124,58,237,.08)",
            border: "1px solid rgba(124,58,237,.28)",
            color: "rgba(248,245,255,.75)",
          }}
        >
          <InfoOutlinedIcon sx={{ color: PURPLE_LIGHT, fontSize: 22, mt: "1px" }} />
          <Typography sx={{ fontSize: 14, lineHeight: 1.7 }}>{t("shippingCalculator.disclaimer")}</Typography>
        </Box>
        </RevealOnScroll>

        <Grid container spacing={3}>
          {/* Input + Result (left) */}
          <Grid item xs={12} md={7}>
            <RevealOnScroll y={24} delay={200}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: "0 24px 64px rgba(0,0,0,.45), 0 0 56px rgba(124,58,237,.14)",
                border: "1.5px solid rgba(124,58,237,.18)",
                overflow: "hidden",
              }}
            >
              {/* Card header */}
              <Box
                sx={{
                  px: { xs: 2.5, md: 3.5 },
                  py: 2.5,
                  background: "linear-gradient(135deg, #2D1B69 0%, #4C1D95 100%)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", width: 36, height: 36 }}>
                    <Inventory2OutlinedIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2} sx={{ color: "#fff" }}>
                      {t("shippingCalculator.parcelDetails")}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>
                      {t("shippingCalculator.parcelDetailsHint")}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <SectionLabel
                  icon={<StraightenIcon sx={{ fontSize: 16 }} />}
                  text={`${t("shippingCalculator.dimensions")} (${sizeUnit})`}
                />
                <Grid container spacing={2} mb={3}>
                  {[
                    { key: "l", label: t("shippingCalculator.length") },
                    { key: "w", label: t("shippingCalculator.width") },
                    { key: "h", label: t("shippingCalculator.height") },
                  ].map((f) => (
                    <Grid item xs={4} key={f.key}>
                      <TextField
                        fullWidth
                        type="number"
                        label={f.label}
                        value={dims[f.key]}
                        onChange={(e) =>
                          setDims({ ...dims, [f.key]: e.target.value })
                        }
                        inputProps={{ min: 0, step: "any" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{sizeUnit}</InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper",
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>

                <SectionLabel
                  icon={<ScaleIcon sx={{ fontSize: 16 }} />}
                  text={t("shippingCalculator.weight")}
                />
                <TextField
                  fullWidth
                  type="number"
                  label={t("shippingCalculator.weight")}
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  inputProps={{ min: 0, step: "any" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{weightUnit}</InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    },
                  }}
                />

                <Divider sx={{ my: 3.5 }} />

                {/* Result */}
                {loading && <LinearProgress color="secondary" />}
                {error && (
                  <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
                {!loading && !error && !result && (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      borderRadius: 3,
                      border: "1.5px dashed rgba(124,58,237,.3)",
                      bgcolor: "rgba(124,58,237,.04)",
                    }}
                  >
                    <CalculateIcon sx={{ fontSize: 40, color: "rgba(124,58,237,.45)", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t("shippingCalculator.enterDetails")}
                    </Typography>
                  </Box>
                )}
                {result?.oversize && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    <Trans
                      i18nKey="shippingCalculator.oversize"
                      values={{
                        total: result.totalSize,
                        max: result.maxSize,
                        unit: sizeUnit,
                      }}
                      components={{
                        contactLink: (
                          <Link
                            component={RouterLink}
                            to="/contact"
                            color="secondary"
                            fontWeight={600}
                          />
                        ),
                      }}
                    />
                  </Alert>
                )}
                {result && !result.oversize && (
                  <Box
                    sx={{
                      borderRadius: 4,
                      p: { xs: 2.5, md: 3 },
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      boxShadow: "0 12px 30px rgba(102,126,234,0.35)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -40, right: -40, width: 160, height: 160,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.08)",
                      }}
                    />

                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LocalShippingIcon />
                        <Typography variant="subtitle1" fontWeight={600}>
                          {t("shippingCalculator.estimatedCost")}
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        icon={ruleMeta(result.rule, t).icon}
                        label={ruleMeta(result.rule, t).text}
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          bgcolor: "rgba(255,255,255,0.22)",
                          backdropFilter: "blur(6px)",
                          "& .MuiChip-icon": { color: "white" },
                        }}
                      />
                    </Stack>

                    <Stack direction="row" alignItems="baseline" spacing={1}>
                      <Typography variant="h2" fontWeight={800} sx={{ lineHeight: 1 }}>
                        {result.total.toLocaleString()}
                      </Typography>
                      <Typography variant="h5" fontWeight={600} sx={{ opacity: 0.85 }}>
                        {currency}
                      </Typography>
                    </Stack>

                    <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.2)" }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {t("shippingCalculator.totalSize")}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {result.totalSize} {sizeUnit}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {t("shippingCalculator.matchedTier")}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          #{result.tier.tier} · ≤{result.tier.size}{sizeUnit}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {t("shippingCalculator.basePrice")}
                        </Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {result.basePrice.toLocaleString()} {currency}
                          {result.appliedTier.tier !== result.tier.tier && (
                            <Typography component="span" variant="caption" sx={{ opacity: 0.8 }}>
                              {" "}(#{result.appliedTier.tier})
                            </Typography>
                          )}
                        </Typography>
                      </Grid>
                      {result.rule === "overweight" && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {t("shippingCalculator.overweightCharge")}
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            +{result.extra.toLocaleString()} {currency}
                            <Typography component="span" variant="caption" sx={{ opacity: 0.8 }}>
                              {" "}({result.extraKg.toFixed(2)}{weightUnit} × {rates.overweightPerKg})
                            </Typography>
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
            </RevealOnScroll>
          </Grid>

          {/* How it works (right) */}
          <Grid item xs={12} md={5}>
            <RevealOnScroll y={24} delay={200}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: "0 24px 64px rgba(0,0,0,.45), 0 0 56px rgba(124,58,237,.14)",
                border: "1.5px solid rgba(124,58,237,.18)",
                overflow: "hidden",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2.5, md: 3.5 },
                  py: 2.5,
                  background: "linear-gradient(135deg, #2D1B69 0%, #4C1D95 100%)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", width: 36, height: 36 }}>
                    <InfoOutlinedIcon />
                  </Avatar>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "#fff" }}>
                    {t("shippingCalculator.howItWorks")}
                  </Typography>
                </Stack>
              </Box>
              <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Stack spacing={2}>
                  {[
                    { color: "#667eea", text: t("shippingCalculator.help.size") },
                    {
                      color: "#f59e0b",
                      text: t("shippingCalculator.help.overweight", {
                        perKg: rates?.overweightPerKg ?? 10,
                        currency,
                      }),
                    },
                    { color: "#10b981", text: t("shippingCalculator.help.normal") },
                    { color: "#3b82f6", text: t("shippingCalculator.help.discount1") },
                    { color: "#8b5cf6", text: t("shippingCalculator.help.discount2") },
                  ].map((item, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          minWidth: 26, height: 26, borderRadius: "50%",
                          bgcolor: `${item.color}1f`,
                          color: item.color,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 13, flexShrink: 0,
                          mt: 0.2,
                        }}
                      >
                        {i + 1}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                        {item.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
            </RevealOnScroll>
          </Grid>

          {/* Rate Table */}
          <Grid item xs={12}>
            <RevealOnScroll y={24} delay={200}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: "0 24px 64px rgba(0,0,0,.45), 0 0 56px rgba(124,58,237,.14)",
                border: "1.5px solid rgba(124,58,237,.18)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2.5, md: 3.5 }, py: 2.5,
                  background: "linear-gradient(135deg, #2D1B69 0%, #4C1D95 100%)",
                }}
              >
                <Typography variant="h6" fontWeight={700} sx={{ color: "#fff" }}>
                  {t("shippingCalculator.rateTable")}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>
                  {t("shippingCalculator.rateTableHint")}
                </Typography>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 440 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper" }}>#</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "background.paper" }}>
                        {t("shippingCalculator.price")} ({currency})
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "background.paper" }}>
                        {t("shippingCalculator.weight")} ({weightUnit})
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "background.paper" }}>
                        {t("shippingCalculator.size")} ({sizeUnit})
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(rates?.tiers || []).map((t2) => {
                      const isMatch = result && !result.oversize && result.tier.tier === t2.tier;
                      const isApplied = result && !result.oversize && result.appliedTier.tier === t2.tier;
                      return (
                        <TableRow
                          key={t2.tier}
                          sx={{
                            bgcolor: isApplied
                              ? "rgba(102,126,234,0.14)"
                              : isMatch
                              ? "rgba(102,126,234,0.05)"
                              : "transparent",
                            transition: "background 0.2s ease",
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" fontWeight={isApplied ? 700 : 500}>
                                {t2.tier}
                              </Typography>
                              {isApplied && (
                                <Chip
                                  label={t("shippingCalculator.applied")}
                                  size="small"
                                  color="secondary"
                                  sx={{ height: 18, fontSize: "0.65rem", fontWeight: 600 }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: isApplied ? 700 : 400 }}>
                            {t2.price.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">{t2.weight}</TableCell>
                          <TableCell align="right">{t2.size}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
            </RevealOnScroll>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  if (isPublic) {
    return (
      <Box sx={{ minHeight: "100vh" }}>
        <PublicHeaderDark />
        {body}
        <PublicFooterDark />
      </Box>
    );
  }

  return <Box sx={{ minHeight: "100vh" }}>{body}</Box>;
}

export default ShippingCalculator;
